import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Loader2, CheckCircle2, Plus, X, CreditCard, Wrench, MessageSquare, FileText } from "lucide-react";
import { InstallPWA } from "@/components/InstallPWA";

const BENEFITS = [
  [CreditCard, "Pay rent online & keep a clear payment history"],
  [Wrench, "Report maintenance in seconds and track the fix"],
  [MessageSquare, "Message your landlord about anything to do with your home"],
  [FileText, "Keep your lease and documents in one secure place"],
];

export default function Onboard() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, login, refreshUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState("intro"); // intro | login | profile
  const [busy, setBusy] = useState(false);
  const [leaseId, setLeaseId] = useState(null);

  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [p, setP] = useState({
    full_name: "", birthdate: "", phone: "", occupants: [], emergency_name: "", emergency_phone: "",
    emergency_relation: "", pets: [], employer_name: "", employer_address: "",
  });

  useEffect(() => {
    if (!token) { setError("This invite link is missing its token."); setLoading(false); return; }
    api.get(`/onboard/${token}`)
      .then((r) => {
        setCtx(r.data);
        setReg((s) => ({ ...s, name: r.data.renter_name || "", email: r.data.renter_email || "" }));
        setCreds((s) => ({ ...s, email: r.data.renter_email || "" }));
        setP((s) => ({ ...s, full_name: r.data.renter_name || "" }));
      })
      .catch((e) => setError(e.response?.data?.detail || "This invite link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  // If a renter is already logged in, link them straight away.
  useEffect(() => {
    if (!ctx || !user || step === "profile") return;
    if (user.role !== "renter") { setError("You're signed in as a non-renter account. Please log out and open this link again."); return; }
    api.post(`/onboard/${token}/link`).then((r) => { setLeaseId(r.data.lease_id); setStep("profile"); toast.success("Your rental is connected"); }).catch(() => {});
  }, [ctx, user]); // eslint-disable-line

  const createAccount = async () => {
    if (!reg.name || !reg.email || !reg.password) { toast.error("Enter your name, email and a password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/onboard/${token}/accept`, { ...reg, email: reg.email.trim().toLowerCase() });
      await refreshUser();
      setLeaseId(data.lease_id);
      setP((s) => ({ ...s, full_name: reg.name }));
      setStep("profile");
      toast.success("Welcome! Your rental is connected.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create your account"); }
    finally { setBusy(false); }
  };

  const doLogin = async () => {
    setBusy(true);
    try {
      await login(creds.email.trim().toLowerCase(), creds.password);
      const { data } = await api.post(`/onboard/${token}/link`);
      setLeaseId(data.lease_id);
      setStep("profile");
      toast.success("Your rental is connected");
    } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const addOccupant = () => setP((s) => ({ ...s, occupants: [...s.occupants, { full_name: "", birthdate: "", phone: "", email: "" }] }));
  const setOcc = (i, k) => (e) => setP((s) => ({ ...s, occupants: s.occupants.map((o, idx) => idx === i ? { ...o, [k]: e.target.value } : o) }));
  const rmOcc = (i) => setP((s) => ({ ...s, occupants: s.occupants.filter((_, idx) => idx !== i) }));
  const addPet = () => setP((s) => ({ ...s, pets: [...s.pets, { name: "", type: "", age: "" }] }));
  const setPet = (i, k) => (e) => setP((s) => ({ ...s, pets: s.pets.map((o, idx) => idx === i ? { ...o, [k]: e.target.value } : o) }));
  const rmPet = (i) => setP((s) => ({ ...s, pets: s.pets.filter((_, idx) => idx !== i) }));
  const setF = (k) => (e) => setP((s) => ({ ...s, [k]: e.target.value }));

  const saveProfile = async () => {
    if (!p.full_name.trim()) { toast.error("Please enter your full name"); return; }
    setBusy(true);
    try {
      await api.put(`/leases/${leaseId}/tenant-profile`, p);
      toast.success("Thanks — you're all set!");
      navigate("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save your details"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-bone"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-bone px-4">
      <div className="bg-white border border-line rounded-2xl p-8 max-w-md text-center" data-testid="onboard-error">
        <h1 className="text-xl font-heading text-ink">Invite unavailable</h1>
        <p className="text-sm text-mute mt-2">{error}</p>
        <Link to="/" className="inline-block mt-4 text-sm text-forest hover:underline">Go to LocalLease home</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bone py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2"><Home className="h-6 w-6 text-forest" /><span className="font-heading text-xl text-ink">LocalLease</span></div>
          <InstallPWA variant="inline" />
        </div>

        {step !== "profile" && (
          <div className="bg-white border border-line rounded-2xl overflow-hidden" data-testid="onboard-intro">
            <div className="bg-forest text-white p-6">
              <p className="text-sm opacity-90">{ctx.landlord_name} invited you</p>
              <h1 className="text-2xl font-heading mt-1">Your home at {ctx.property_title} is moving to LocalLease</h1>
              {ctx.property_address && <p className="text-sm opacity-90 mt-1">{ctx.property_address}</p>}
            </div>
            <div className="p-6">
              <p className="text-sm text-ink">{ctx.landlord_name} is using LocalLease from now on to track rent, handle maintenance, and stay in touch — all in one friendly place. Set up your free tenant account to come along. It takes about a minute.</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                {BENEFITS.map(([Icon, t]) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-ink"><Icon className="h-4 w-4 text-forest mt-0.5 shrink-0" />{t}</li>
                ))}
              </ul>

              {step === "intro" && (
                <div className="mt-6 space-y-3" data-testid="onboard-create">
                  <p className="text-xs font-semibold uppercase tracking-wide text-forest">Create your free account</p>
                  <div><Label>Full name</Label><Input data-testid="ob-name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} className="mt-1 border-line" /></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input data-testid="ob-email" type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className="mt-1 border-line" /></div>
                    <div><Label>Create a password</Label><Input data-testid="ob-password" type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="mt-1 border-line" /></div>
                  </div>
                  <button data-testid="ob-create-btn" onClick={createAccount} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-forest text-white rounded-lg py-2.5 text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Create account & connect my rental
                  </button>
                  <p className="text-center text-sm text-mute">Already have an account? <button data-testid="ob-goto-login" onClick={() => setStep("login")} className="text-forest hover:underline">Log in</button></p>
                </div>
              )}

              {step === "login" && (
                <div className="mt-6 space-y-3" data-testid="onboard-login">
                  <p className="text-xs font-semibold uppercase tracking-wide text-forest">Log in to connect this rental</p>
                  <div><Label>Email</Label><Input data-testid="ob-login-email" type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className="mt-1 border-line" /></div>
                  <div><Label>Password</Label><Input data-testid="ob-login-password" type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className="mt-1 border-line" /></div>
                  <button data-testid="ob-login-btn" onClick={doLogin} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-forest text-white rounded-lg py-2.5 text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Log in & connect
                  </button>
                  <p className="text-center text-sm text-mute">New here? <button onClick={() => setStep("intro")} className="text-forest hover:underline">Create an account</button></p>
                </div>
              )}
            </div>
          </div>
        )}

        {step === "profile" && (
          <div className="bg-white border border-line rounded-2xl p-6" data-testid="onboard-profile">
            <h1 className="text-xl font-heading text-ink">A few details about your household</h1>
            <p className="text-sm text-mute mt-1">Only your full name is required — add whatever else you'd like now, or finish it later from your dashboard.</p>

            <div className="mt-5 space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Full name *</Label><Input data-testid="tp-name" value={p.full_name} onChange={setF("full_name")} className="mt-1 border-line" /></div>
                <div><Label>Birthdate</Label><Input data-testid="tp-birthdate" type="date" value={p.birthdate} onChange={setF("birthdate")} className="mt-1 border-line" /></div>
                <div><Label>Phone</Label><Input data-testid="tp-phone" value={p.phone} onChange={setF("phone")} className="mt-1 border-line" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between"><Label>Other tenants at the residence</Label><button data-testid="tp-add-occupant" onClick={addOccupant} className="text-sm text-forest inline-flex items-center gap-1 hover:underline"><Plus className="h-3.5 w-3.5" /> Add</button></div>
                {p.occupants.map((o, i) => (
                  <div key={i} className="mt-2 grid sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                    <Input placeholder="Full name" value={o.full_name} onChange={setOcc(i, "full_name")} className="border-line" />
                    <Input type="date" value={o.birthdate} onChange={setOcc(i, "birthdate")} className="border-line" />
                    <Input placeholder="Phone" value={o.phone} onChange={setOcc(i, "phone")} className="border-line" />
                    <Input placeholder="Email" value={o.email} onChange={setOcc(i, "email")} className="border-line" />
                    <button onClick={() => rmOcc(i)} className="text-mute hover:text-terracotta"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Emergency contact</Label><Input data-testid="tp-emg-name" placeholder="Name" value={p.emergency_name} onChange={setF("emergency_name")} className="mt-1 border-line" /></div>
                <div><Label>Their phone</Label><Input placeholder="Phone" value={p.emergency_phone} onChange={setF("emergency_phone")} className="mt-1 border-line" /></div>
                <div><Label>Relationship</Label><Input placeholder="e.g. Sister" value={p.emergency_relation} onChange={setF("emergency_relation")} className="mt-1 border-line" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between"><Label>Pets</Label><button data-testid="tp-add-pet" onClick={addPet} className="text-sm text-forest inline-flex items-center gap-1 hover:underline"><Plus className="h-3.5 w-3.5" /> Add</button></div>
                {p.pets.map((o, i) => (
                  <div key={i} className="mt-2 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                    <Input placeholder="Name" value={o.name} onChange={setPet(i, "name")} className="border-line" />
                    <Input placeholder="Type (dog, cat…)" value={o.type} onChange={setPet(i, "type")} className="border-line" />
                    <Input placeholder="Age" value={o.age} onChange={setPet(i, "age")} className="border-line" />
                    <button onClick={() => rmPet(i)} className="text-mute hover:text-terracotta"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Place of employment</Label><Input data-testid="tp-employer" value={p.employer_name} onChange={setF("employer_name")} className="mt-1 border-line" /></div>
                <div><Label>Employer address</Label><Input value={p.employer_address} onChange={setF("employer_address")} className="mt-1 border-line" /></div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button data-testid="tp-skip" onClick={() => navigate("/dashboard")} className="text-sm border border-line rounded-lg px-4 py-2 text-ink hover:bg-bone">Skip for now</button>
              <button data-testid="tp-save" onClick={saveProfile} disabled={busy} className="inline-flex items-center gap-2 bg-forest text-white rounded-lg px-5 py-2 text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save & go to dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
